import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Form as FinalForm } from 'react-final-form'
import { useRouter } from 'next/router'
import { exchangeCodeForToken } from '../../services'
import Autosave from './autosave'
import styled from '@emotion/styled'
import axios from 'axios'
import { getSchema, getFormComponent } from './utils'
import { storageMode, normalizePhoneNumber } from '../../helpers'
import SubmitBtn from './submit-btn'
import {
  googleAnalyticsSet,
  googleTagManagerEvent,
  segmentEvent,
} from '../../helpers/tracking'
import { capitalize } from 'lodash'
import { validate } from './utils'
import FormConfirmation from './form-confirmation'
import { useUserContext } from '../UserContext'
import Turnstile, { useTurnstile } from "react-turnstile"
import Bugsnag from '@bugsnag/js'
import OTPVerificationModal from '../OTPVerificationModal'

const FormContent = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  box-sizing: border-box;
`

const validTurnstileErrorStates = [
  '102',
  '103',
  '104',
  '106',
  '11060',
  '11062',
  '300',
  '600'
]

/* FormController:
 *
 * Component to embed on a Page to display
 * a form that submits to a Notion database
 * specific to the formName passed in
 *
 * Tracks page id for interaction
 *
 * To create a form:
 *  - add the initial form state to the ./utils.js
 *    that matches the Notion Database
 *
 *  - update getSchema to return that object
 *    for the formName
 *
 *  - this sets up for the form state to match Notion
 *    and to auto submit properly
 *
 *  - create a component with Fields using the form state
 *
 *  - return it from getFormComponent
 *
 *  - embed FormController on a page and pass formName
 *
 */
const FormController = ({ formName, headerRef, enableAutosave = true }) => {
  const { query } = useRouter()

  // no pageId creates a row in notion
  const [pageId, setPageId] = useState(null)
  const [savingPageId, setSavingPageId] = useState(false)
  const [submittedValues, setSubmittedValues] = useState(null)
  const lastRequest = useRef(null)
  const storage = storageMode('localStorage')

  const [displayConfirmation, setDisplayConfirmation] = useState(false)

  // save ref of all fields to scroll to required
  const fieldRefs = useRef({})

  const eventAttributes = { lead_source: `${capitalize(formName)} Form` }

  // track a form event to segment, GTM, and optionally google ads
  const trackFormEvent = (eventName, userData, { withGoogleAds = false } = {}) => {
    try {
      segmentEvent(eventName, userData)
      googleTagManagerEvent(eventName, { ...eventAttributes, ...userData })
      googleAnalyticsSet('user_data', userData)

      if (withGoogleAds) {
        window.gtag && window.gtag('event', 'conversion', {
          send_to: 'AW-983912077/y5aKCK74mvkZEI2dldUD',
        })
      }
    } catch (error) {
      console.log(error)
    }
  }

  // track conversion event only when server confirms phone was verified
  const trackConfirmEvent = (res, userData) => {
    if (!res.data?.phoneVerified) {
      return
    }
    trackFormEvent(`${formName}_form_confirm`, userData, { withGoogleAds: true })
  }

  // handle sending form data to notion endpoint
  // first request will await for a pageId
  // all requests after will be just sent
  const save = async (values, isSubmitted = false, cloudFlareToken = null, verifyToken = null) => {
    const properties = {
      ...values,
    }

    if (properties['First Name'] && properties['Last Name']) {
      properties['Opportunity'] =
        `${properties['First Name']} ${properties['Last Name']}`
    }

    const userData = {
      email: properties['Email'],
      phone_number: properties['Phone Number'],
      address: {
        first_name: properties['First Name'],
        last_name: properties['Last Name'],
        street: properties['Event Location'] || properties['Address'],
      },
    }

    // only track on final submit
    if (isSubmitted) {
      segmentEvent(`generate_lead_${formName}`, { ...eventAttributes, ...userData })
      trackFormEvent(`${formName}_form_submit`, userData)
    }
  
    // notion doesn't like incomplete fields
    // remove empty strings and empty arrays
    Object.keys(properties).forEach((key) => {
      const value = properties[key];

      if (value === '' || value == null || (Array.isArray(value) && value.length === 0)) {
        delete properties[key];
      }
    })

    const req = {
      method: 'POST',
      url: `/api/events/${formName}`,
      data: {
        pageId,
        formName,
        isSubmitted,
        properties,
        verifyToken,
        ...(cloudFlareToken ? { cloudflareToken: cloudFlareToken } : {}),
      },
    }

    // only save if we have email & phone
    if (!properties?.Email || !properties?.['Phone Number']) {
      return
    }

    setSubmittedValues(properties)

    // if pageId exists no need to wait for response
    if (pageId) {
      axios(req).then(res => trackConfirmEvent(res, userData))
      return
    }

    if (savingPageId) {
      return (lastRequest.current = req)
    }

    setSavingPageId(true)

    // once we receive the pageId store it
    try {
      const res = await axios(req)
      if (res.data?.id) {
        setPageId(res.data.id)
        storage.setItem('pageId', res.data.id)
      }

      trackConfirmEvent(res, userData)

      if (lastRequest.current) {
        const body = {
          ...lastRequest.current,
          data: {
            ...lastRequest.current.data,
            pageId: res.data?.id,
          },
        }

        axios(body)
        lastRequest.current = null
      }
    } catch (err) {
      console.log(err)
    }
  }

  // specific submit function for submit button
  // this way we can pass isSubmitted
  // and toggle the confirmation banner only when pressing this button
  const handleSubmit = async (values, cloudFlareToken, verifyToken, onComplete) => {

    try {

      if (query?.origin) {
        values = { ...values, ['Event Source']: query.origin.split('-').join(' ') }
      }

      await save({ ...values, ['Is Complete']: true }, true, cloudFlareToken, verifyToken)
      setDisplayConfirmation(true)
    } catch (e) {
      console.log(e)
    } finally {
      // reset cloudflare execution state that's keeping the button disabled
      // this must run even if save() throws an error
      onComplete && onComplete()
    }
  }

  useEffect(() => {
    const item = storage.getItem('pageId')
    if (!item) {
      return
    }

    setPageId(item)
  }, [storage])

  // react-final-form compares `validate` by identity (useWhenValueChanges) and
  // calls form.setConfig('validate') whenever it changes, which re-runs
  // validation over every value and re-notifies subscribers. An inline arrow
  // here meant that happening on every render of this component, and this
  // component re-renders on each autosave -- which fires on every field blur.
  // With the subscription above in place this costs redundant validation work
  // rather than extra renders, but there is no reason to pay it. `onSubmit`
  // needs no such treatment: setConfig('onSubmit') only assigns.
  const validateForForm = useCallback(
    (values) => validate(values, formName),
    [formName],
  )

  // get notion schema for formName to use as initial form state
  const initialValues = getSchema(formName)
  const FormComponent = getFormComponent(formName)

  if (displayConfirmation) {
    //  hide form header
    if (headerRef?.current) {
      headerRef.current.style.display = 'none'
    }

    return (
      <div>
        <FormConfirmation
          formName={formName}
          values={submittedValues}
          pageId={pageId}
        />
      </div>
    )
  }

  return (
    <FinalForm
      onSubmit={handleSubmit}
      initialValues={initialValues}
      // Without a subscription this render prop wakes on every slice of form
      // state, so a keystroke in any field re-rendered the whole form tree --
      // every Field, and the field components under them. `submitting` is the
      // only piece of form state the inner Form actually reads; the pieces the
      // rest of the tree needs are subscribed for individually (Autosave's
      // FormSpy, SubmitBtn and each field's own useFormState).
      subscription={{ submitting: true }}
      validate={validateForForm}
      render={(props) => (
        <Form
          {...props}
          formName={formName}
          FormComponent={FormComponent}
          autoSave={save}
          fieldRefs={fieldRefs}
          enableAutosave={enableAutosave}
          handleSubmit={handleSubmit}
        />
      )}
    />
  )
}

/* Form:
 *
 * returns the actual form to be displayed to user
 * receives props from FinalForm component as well
 * as which Form component to display
 * handles updating form state once a user is loaded
 * if they are authenticated
 */
const Form = ({
  handleSubmit,
  form,
  FormComponent,
  formName,
  submitting,
  autoSave,
  fieldRefs,
  enableAutosave
}) => {
  const turnstile = useTurnstile()
  const [executingChallenge, setExecutingChallenge] = useState(false)
  const [challengeAttempts, setChallengeAttempts] = useState(0)
  const cloudflareTokenRef = useRef(null)

  const router = useRouter()
  const { user, setUser } = useUserContext()

  // only auto-fill user info 1 time
  const [hasAutoFilledUser, setHasAutoFilledUser] = useState(false)

  // OTP verification state
  const [showOTPModal, setShowOTPModal] = useState(false)
  // The phone number that actually completed OTP, normalized. Verification is
  // bound to a number rather than tracked as a bare boolean: the proof token
  // minted by /api/verify/check only validates the number it was issued for, so
  // a client who verifies one number and then edits the field must go back
  // through the modal instead of submitting a token the server will reject.
  const [verifiedPhoneNumber, setVerifiedPhoneNumber] = useState(null)
  const [verifyToken, setVerifyToken] = useState(null)

  /**
   * needsPhoneVerification: whether the form carries a phone number that has not
   * completed OTP verification. An unreadable number counts as unverified, so
   * the check fails closed.
   *
   * @param {object} values - current form values
   * @return {boolean}
   */
  const needsPhoneVerification = values => {
    const phoneNumber = values['Phone Number']
    if (!phoneNumber) {
      return false
    }
    const normalized = normalizePhoneNumber(phoneNumber)
    return !normalized || normalized !== verifiedPhoneNumber
  }

  // handle logged in user from iOS app
  useEffect(() => {
    if (!router?.query['authorization-code'] || !!user) {
      return
    }

    // extract authorization code
    const { ['authorization-code']: code, ...query } = router.query

    // get user from token exchange, set user in context,
    // and remove authorization-code from query string
    // leaving the rest intact
    exchangeCodeForToken(code)
      .then(setUser)
      .then(() => {
        router.replace({ pathName: router.pathname, query })
      })
  }, [router, setUser, user])

  // once user has loaded update form state
  useEffect(() => {
    if (!user || !!hasAutoFilledUser) {
      return
    }

    form.batch(() => {
      form.change('First Name', user.nameFirst),
      form.change('Last Name', user.nameLast),
      form.change('Email', user.email),
      form.change('Phone Number', user.phone)
      // do not auto-fill a second time
      setHasAutoFilledUser(true)
    })
  }, [user, form, hasAutoFilledUser])

  return (
    <>
      <form onSubmit={(e) => {
        e.preventDefault()

        // Prevent double submission
        if (executingChallenge || submitting) {
          return
        }

        // Check if form is valid before running Turnstile
        const formState = form.getState()

        if (formState?.hasValidationErrors || !formState?.valid) {
          form.submit()
          return
        }

        // Check if phone verification is needed AFTER validation
        if (needsPhoneVerification(formState.values)) {
          // Execute Turnstile challenge before showing OTP modal
          setExecutingChallenge(true)
          turnstile.execute()
          return
        }

        // run cloudflare challenge on form submission
        setExecutingChallenge(true)
        turnstile.execute()
      }}>
        <Turnstile
          execution='execute'
          sitekey="0x4AAAAAAB0dIAMjgrza8_XP"
          onVerify={(token) => {
            const formState = form.getState()

            // Store the token for reuse (using ref for synchronous access)
            cloudflareTokenRef.current = token

            // If phone verification is needed, show OTP modal
            if (needsPhoneVerification(formState.values)) {
              setShowOTPModal(true)
              setExecutingChallenge(false)
              return
            }

            // Otherwise submit form with token from challenge
            handleSubmit(formState.values, token, verifyToken, () => setExecutingChallenge(false))
          }}
          onError={(error) => {
            // cloudflare recommends retrying on specific errors
            const isValidError = error && (typeof error === 'string') && !!validTurnstileErrorStates.find((e) => {
              return error.startsWith(e)
            })

            // 3 attempts before submitting as an un-verified human
            if (isValidError && challengeAttempts < 2) {
              turnstile.reset()
              setChallengeAttempts(challengeAttempts + 1)
              return setTimeout(() => turnstile.execute(), 300)
            }

            // if we've hit the max number of attempts just submit the form without a token
            Bugsnag.notify(new Error('Turnstile Challenge Failed'), (event) => {
              event.severity = 'warning'
              event.addMetadata('turnstile', {
                error: error,
                form: formName || 'unknown'
              })
            })

            // submit form without token if error is due to a failed challenge
            // and let the route handle it
            if (executingChallenge) {
              const formState = form.getState()

              // Clear the token on error
              cloudflareTokenRef.current = null

              // If phone verification is needed, show OTP modal without token
              if (needsPhoneVerification(formState.values)) {
                setShowOTPModal(true)
                setExecutingChallenge(false)
                setChallengeAttempts(0)
                return
              }

              handleSubmit(formState.values, null, verifyToken, () => {
                setExecutingChallenge(false)
                setChallengeAttempts(0)
              })
            }
          }}
        />
        <FormContent>
          <Autosave enabled={enableAutosave} save={autoSave} />
          <FormComponent disabled={submitting} fieldRefs={fieldRefs} />
          <SubmitBtn
            submitting={submitting || executingChallenge}
            fieldRefs={fieldRefs}
          />
        </FormContent>
      </form>

      <OTPVerificationModal
        isOpen={showOTPModal}
        closeModal={() => setShowOTPModal(false)}
        phoneNumber={form.getState().values['Phone Number']}
        cloudflareTokenRef={cloudflareTokenRef}
        onVerificationSuccess={({ verifyToken }) => {
          setVerifyToken(verifyToken)
          setVerifiedPhoneNumber(
            normalizePhoneNumber(form.getState().values['Phone Number'])
          )
          setShowOTPModal(false)

          // Automatically submit form after verification
          // Reset Turnstile and challenge attempts to start fresh for final submission
          setTimeout(() => {
            setChallengeAttempts(0)
            turnstile.reset()
            setExecutingChallenge(true)
            turnstile.execute()
          }, 100)
        }}
        isDesktop={true}
      />
    </>
  )
}

export default FormController
